import * as React from "react";
import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import styled from "styled-components";
import { Select, Spinner, Switch } from "components/common";
import { Button, HelpBlock, Input } from "components/bootstrap";
import IGNORE_AUTOFILL from "aiagent/components/passwordManagerAutofill";
import useBedrockRegions from "aiagent/hooks/useBedrockRegions";
const ResetButton = styled(Button)`
  float: right;
`;
const ToggleRow = styled.div`
  margin-bottom: ${({ theme }) => theme.spacings.sm};
`;
const storedApiKey = (auth) => (auth == null ? void 0 : auth.type) === "api_key" ? auth.api_key : void 0;
const storedToken = (auth) => (auth == null ? void 0 : auth.type) === "bedrock_api_token" ? auth.token : void 0;
const storedAccessKeyId = (auth) => (auth == null ? void 0 : auth.type) === "bedrock_aws_credentials" ? auth.access_key_id : void 0;
const storedSecretKey = (auth) => (auth == null ? void 0 : auth.type) === "bedrock_aws_credentials" ? auth.secret_key : void 0;
const BedrockRegionField = () => {
  var _a, _b;
  const { values, setFieldValue } = useFormikContext();
  const auth = (_a = values.credentials) == null ? void 0 : _a.auth;
  const region = auth && "region" in auth ? (_b = auth.region) != null ? _b : "" : "";
  const { data: regions, isLoading } = useBedrockRegions();
  return /* @__PURE__ */ React.createElement(Input, { id: "bedrock-region", label: "AWS Region" }, isLoading ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading AWS regions..." }) : /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: "bedrock-region",
      placeholder: "Select a region",
      options: regions.map((value) => ({ value, label: value })),
      value: region,
      onChange: (value) => setFieldValue("credentials.auth.region", value),
      clearable: false,
      inputProps: IGNORE_AUTOFILL
    }
  ));
};
const OllamaBaseUrlField = () => {
  var _a, _b;
  const { values, setFieldValue } = useFormikContext();
  const auth = (_a = values.credentials) == null ? void 0 : _a.auth;
  const baseUrl = (auth == null ? void 0 : auth.type) === "ollama" ? (_b = auth.base_url) != null ? _b : "" : "";
  return /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials.auth.base_url",
      name: "credentials.auth.base_url",
      type: "text",
      label: "Base URL",
      placeholder: "http://localhost:11434",
      value: baseUrl,
      onChange: ({ target: { value } }) => setFieldValue("credentials.auth.base_url", value),
      help: "Where Ollama is listening. Graylog reaches it directly, so it has to be resolvable from the server."
    }
  );
};
const EncryptedSecretField = ({ name, label, isSet, autoComplete = void 0 }) => {
  const { setFieldValue } = useFormikContext();
  const [showReset, setShowReset] = useState(isSet);
  useEffect(() => {
    if (isSet) {
      setFieldValue(name, { keep_value: true });
    }
  }, [isSet, name, setFieldValue]);
  const toggleReset = () => {
    if (showReset) {
      setFieldValue(name, { delete_value: true });
      setShowReset(false);
      return;
    }
    setFieldValue(name, { keep_value: true });
    setShowReset(true);
  };
  if (showReset) {
    return /* @__PURE__ */ React.createElement(Input, { id: `${name}-reset`, label }, /* @__PURE__ */ React.createElement(ResetButton, { onClick: toggleReset }, "Reset key"));
  }
  return /* @__PURE__ */ React.createElement(
    Input,
    {
      id: name,
      name,
      type: "password",
      label,
      autoComplete,
      onChange: ({ target: { value } }) => setFieldValue(name, { set_value: value }),
      buttonAfter: isSet ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: toggleReset }, "Undo Reset") : void 0
    }
  );
};
const BedrockCredentialFields = ({ storedAuth = void 0 }) => {
  var _a, _b, _c, _d;
  const { values, setFieldValue } = useFormikContext();
  const auth = (_a = values.credentials) == null ? void 0 : _a.auth;
  const useApiToken = (auth == null ? void 0 : auth.type) !== "bedrock_aws_credentials";
  const onToggle = (checked) => {
    const region = auth && "region" in auth ? auth.region : void 0;
    setFieldValue(
      "credentials.auth",
      checked ? { type: "bedrock_api_token", region } : { type: "bedrock_aws_credentials", region }
    );
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ToggleRow, null, /* @__PURE__ */ React.createElement(
    Switch,
    {
      id: "bedrock-use-api-token",
      label: "Use AWS Bedrock API Token",
      checked: useApiToken,
      onChange: (event) => onToggle(event.target.checked)
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, useApiToken ? "A Bedrock API key, sent as a bearer token." : "An IAM access key pair.")), useApiToken ? /* @__PURE__ */ React.createElement(
    EncryptedSecretField,
    {
      key: "bedrock-token",
      name: "credentials.auth.token",
      label: "Bedrock API Token",
      isSet: !!((_b = storedToken(storedAuth)) == null ? void 0 : _b.is_set),
      autoComplete: "section-bedrock current-password"
    }
  ) : /* @__PURE__ */ React.createElement(React.Fragment, { key: "bedrock-aws" }, /* @__PURE__ */ React.createElement(
    EncryptedSecretField,
    {
      name: "credentials.auth.access_key_id",
      label: "Access Key ID",
      isSet: !!((_c = storedAccessKeyId(storedAuth)) == null ? void 0 : _c.is_set),
      autoComplete: "section-bedrock-aws username"
    }
  ), /* @__PURE__ */ React.createElement(
    EncryptedSecretField,
    {
      name: "credentials.auth.secret_key",
      label: "Secret Access Key",
      isSet: !!((_d = storedSecretKey(storedAuth)) == null ? void 0 : _d.is_set),
      autoComplete: "section-bedrock-aws current-password"
    }
  )), /* @__PURE__ */ React.createElement(BedrockRegionField, null));
};
const ProviderCredentialFields = ({ provider, storedAuth = void 0 }) => {
  var _a;
  if (provider === "bedrock") {
    return /* @__PURE__ */ React.createElement(BedrockCredentialFields, { storedAuth });
  }
  if (provider === "ollama") {
    return /* @__PURE__ */ React.createElement(OllamaBaseUrlField, null);
  }
  return /* @__PURE__ */ React.createElement(
    EncryptedSecretField,
    {
      name: "credentials.auth.api_key",
      label: "API Key",
      isSet: !!((_a = storedApiKey(storedAuth)) == null ? void 0 : _a.is_set),
      autoComplete: `section-${provider} current-password`
    }
  );
};
export default ProviderCredentialFields;
